import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Monitor, Barcode, ShoppingBag, Package, ShoppingCart, Settings } from 'lucide-react';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import OTPConfirmationModal from './OTPConfirmationModal';
import { addNotification } from '../services/notificationService';
import notificationService from '../services/notificationService';
import notificationApiService from '../services/notificationApiService';

const POSView2 = ({
  searchTerm,
  setSearchTerm,
  handleKeyDown,
  handleSearchFocus,
  isSearching,
  searchResults,
  selectedIndex,
  handleProductSelect,
  isQuantityFocused,
  isUomFocused,
  quantity,
  setQuantity,
  setIsQuantityFocused,
  setIsUomFocused,
  uomValue,
  setUomValue,
  selectedProduct,
  cartItems,
  setCartItems,
  paymentMethod,
  setPaymentMethod,
  paidAmount,
  setPaidAmount,
  processSale,
  isProcessingSale,
  handleHoldSales,
  heldSales,
  setShowHeldSalesModal,
  handleCancel,
  updateHeldSales,
  handleLoadHeldSale,
  formatCurrency,
  currentTime,
  transactionCount,
  user,
  systemStatus,
  showConfirmation,
  handleReturnSale,
  addToCart
}) => {

  
  // POS View2 specific state
  const [posProducts, setPosProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productsLoadedAt, setProductsLoadedAt] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isInQuantityFlow, setIsInQuantityFlow] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  
  // OTP Confirmation states
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpAction, setOtpAction] = useState(null);
  const [otpItemData, setOtpItemData] = useState(null);
  
  
  
  // Held Sales Modal state
  const [showHeldSalesModalLocal, setShowHeldSalesModalLocal] = useState(false);
  const [deletingSaleId, setDeletingSaleId] = useState(null);
  const [localHeldSales, setLocalHeldSales] = useState([]);

  // Component mounted
  useEffect(() => {
    console.log('✅ POSView2 component mounted');
  }, []);

  // Fetch products when POSView2 mounts
  useEffect(() => {
    const fetchPOSProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const response = await apiService.getPOSView2Products({
          limit: 100,
          in_stock_only: true
        });
        
        if (response.success && response.products) {
          setPosProducts(response.products);
          setFilteredProducts(response.products); // Initialize filtered products with all products
          setProductsLoadedAt(response.metadata?.loaded_at || new Date().toISOString());
          console.log(`POS View2: Loaded ${response.count} products`);
          toast.success(`Loaded ${response.count} products for POS`, {
            duration: 2000,
            position: 'top-right'
          });
        }
      } catch (error) {
        console.error('Error fetching POS products:', error);
        toast.error('Failed to load products. Please refresh.', {
          duration: 3000,
          position: 'top-right'
        });
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchPOSProducts();
  }, []); // Empty dependency array means this runs once when component mounts

  // F2, F4, and Spacebar key handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        const searchInput = document.querySelector('.search-container input');
        if (searchInput) {
          searchInput.focus();
          // Clear quantity flow mode when focusing search
          if (isInQuantityFlow) {
            setIsInQuantityFlow(false);
            setSearchTerm('');
            setSelectedProductName('');
          }
        }
      } else if (e.key === 'F4') {
        e.preventDefault();
        const paidAmountInput = document.getElementById('paidAmount-input');
        if (paidAmountInput) {
          paidAmountInput.focus();
          // Ensure text is selected after focus
          setTimeout(() => {
            paidAmountInput.select();
            paidAmountInput.setSelectionRange(0, paidAmountInput.value.length);
          }, 50);
        }
      } else if (e.key === ' ' && e.target.id === 'paidAmount-input') {
        // Spacebar pressed while paid amount field is focused
        e.preventDefault();
        if (cartItems.length > 0 && paidAmount > 0) {
          handleProcessSaleView2();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isInQuantityFlow, cartItems, paidAmount]);

  // Filter products based on search term (disabled during quantity flow)
  useEffect(() => {
    if (isInQuantityFlow) {
      // During quantity flow, don't filter - show all products
      setFilteredProducts(posProducts);
    } else if (!searchTerm || searchTerm.trim() === '') {
      // If no search term, show all products
      setFilteredProducts(posProducts);
    } else {
      // Filter products based on search term
      const filtered = posProducts.filter(product => {
        const searchLower = searchTerm.toLowerCase();
        return (
          product.name.toLowerCase().includes(searchLower) ||
          (product.productID && product.productID.toLowerCase().includes(searchLower))
        );
      });
      setFilteredProducts(filtered);
    }
  }, [searchTerm, posProducts, isInQuantityFlow]);

  // Debug effect to monitor searchTerm changes
  useEffect(() => {
    console.log('SearchTerm changed:', searchTerm, 'isInQuantityFlow:', isInQuantityFlow);
  }, [searchTerm, isInQuantityFlow]);

  // Sync local held sales with prop
  useEffect(() => {
    setLocalHeldSales(heldSales);
  }, [heldSales]);

  // Handle successful sale for View2
  const handleProcessSaleView2 = async () => {
    try {
      // Call the original processSale function
      await processSale();
      
      // Show success notification
      const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
      notificationService.showSaleProcessedNotification(totalAmount, cartItems.length);
    } catch (error) {
      console.error('Error processing sale in View2:', error);
    }
  };

  // OTP Confirmation handlers
  const handleOTPConfirm = async () => {
    if (!otpAction) return;

    try {
      switch (otpAction.type) {
        case 'remove_cart_item':
          await handleRemoveCartItemConfirmed(otpAction.itemId);
          break;
        case 'clear_cart':
          await handleClearCartConfirmed();
          break;
        case 'delete_held_sale':
          await handleDeleteHeldSaleConfirmed(otpAction.saleId);
          break;
        default:
          console.error('Unknown OTP action type:', otpAction.type);
      }
    } catch (error) {
      console.error('Error executing OTP confirmed action:', error);
      throw error;
    }
  };

  const handleRemoveCartItemConfirmed = async (itemId) => {
    // Get the item before removing it
    const item = cartItems.find(item => item.id === itemId);
    
    // Remove item from cart
    setCartItems(prev => prev.filter(item => item.id !== itemId));
    
    // Show toast notification
    if (item) {
      toast.success(`${item.product.name} removed from cart`, {
        icon: '🗑️',
        duration: 2000,
      });
    }
    
    // Close item modal if it was open for this item
    if (selectedCartItem && selectedCartItem.id === itemId) {
      setShowItemModal(false);
      setSelectedCartItem(null);
    }
    
    // Update notification status to approved after successful removal
    try {
      // Find the OTP notification for this action and mark it as approved
      const notifications = notificationService.getNotifications();
      console.log('🔍 Looking for OTP notification for item removal');
      console.log('🔍 Available notifications:', notifications.map(n => ({
        id: n.id,
        title: n.title,
        actionType: n.metadata?.actionType,
        itemName: n.metadata?.itemName,
        isOTP: n.metadata?.isOTP
      })));
      
      // Find the most recent OTP notification for remove_cart_item action
      const otpNotifications = notifications.filter(n => 
        n.metadata?.isOTP && 
        n.metadata?.actionType === 'remove_cart_item' &&
        n.title?.includes('Verification Code')
      );
      
      // Get the most recent one (first in the array since they're sorted by newest first)
      const otpNotification = otpNotifications[0];
      
      if (otpNotification) {
        await notificationApiService.markAsApproved(otpNotification.id);
        console.log('✅ Notification marked as approved after item removal');
        
        // Update local notification state
        const updatedNotifications = notifications.map(notification => 
          notification.id === otpNotification.id 
            ? { ...notification, status: 'approved' }
            : notification
        );
        notificationService.notifications = updatedNotifications;
        notificationService.saveNotifications();
        
        // Force refresh notifications in the UI
        await notificationService.loadNotifications();
      } else {
        console.log('❌ No OTP notification found to mark as approved for item removal');
      }
    } catch (statusError) {
      console.error('Error updating notification status:', statusError);
    }
  };

  const handleClearCartConfirmed = async () => {
    const itemCount = cartItems.length;
    const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
    
    // Clear all items from cart
    setCartItems([]);
    
    // Close any open modals
    setShowItemModal(false);
    setSelectedCartItem(null);
    
    // Show toast notification only
    toast.success(`Cart cleared! ${itemCount} items removed (Total: ₵${totalAmount.toFixed(2)})`, {
      icon: '🗑️',
      duration: 3000,
    });
    
    // Update notification status to approved after successful cart clearing
    try {
      // Find the OTP notification for this action and mark it as approved
      const notifications = notificationService.getNotifications();
      console.log('🔍 Looking for OTP notification for cart clearing');
      console.log('🔍 Total notifications:', notifications.length);
      console.log('🔍 Available notifications:', notifications.map(n => ({
        id: n.id,
        title: n.title,
        status: n.status,
        actionType: n.metadata?.actionType,
        itemName: n.metadata?.itemName,
        isOTP: n.metadata?.isOTP
      })));
      
      // Find the most recent OTP notification for clear_cart action
      const otpNotifications = notifications.filter(n => 
        n.metadata?.isOTP && 
        n.metadata?.actionType === 'clear_cart' &&
        n.title?.includes('Verification Code')
      );
      
      console.log('🔍 Filtered OTP notifications for clear_cart:', otpNotifications.length);
      console.log('🔍 OTP notifications details:', otpNotifications.map(n => ({
        id: n.id,
        title: n.title,
        status: n.status,
        actionType: n.metadata?.actionType
      })));
      
      // Get the most recent one (first in the array since they're sorted by newest first)
      const otpNotification = otpNotifications[0];
      
      if (otpNotification) {
        console.log('✅ Found OTP notification to update:', {
          id: otpNotification.id,
          title: otpNotification.title,
          currentStatus: otpNotification.status
        });
        
        await notificationApiService.markAsApproved(otpNotification.id);
        console.log('✅ API call to mark as approved completed');
        
        // Update local notification state
        const updatedNotifications = notifications.map(notification => 
          notification.id === otpNotification.id 
            ? { ...notification, status: 'approved' }
            : notification
        );
        console.log('✅ Updated notification in local array:', updatedNotifications.find(n => n.id === otpNotification.id));
        
        notificationService.notifications = updatedNotifications;
        notificationService.saveNotifications();
        console.log('✅ Saved notifications to localStorage');
        
        // Force refresh notifications in the UI
        await notificationService.loadNotifications();
        console.log('✅ Reloaded notifications from API');
        
        // Verify the update
        const verifyNotifications = notificationService.getNotifications();
        const verifyNotification = verifyNotifications.find(n => n.id === otpNotification.id);
        console.log('✅ Verified notification status after reload:', {
          id: verifyNotification?.id,
          status: verifyNotification?.status
        });
      } else {
        console.log('❌ No OTP notification found to mark as approved for cart clearing');
      }
    } catch (statusError) {
      console.error('❌ Error updating notification status:', statusError);
      console.error('❌ Error stack:', statusError.stack);
    }
    
    // Don't call handleCancel() as it might show its own confirmation modal
    // Instead, just reset the payment-related states directly
    setPaidAmount(0);
    setPaymentMethod('Cash');
  };

  const handleDeleteHeldSaleConfirmed = async (saleId) => {
    try {
      setDeletingSaleId(saleId);
      
      // Find the held sale index to remove from local state
      const saleIndex = localHeldSales.findIndex(sale => 
        (sale.reference || `HOLD-${localHeldSales.indexOf(sale) + 1}`) === saleId
      );
      
      if (saleIndex === -1) {
        toast.error('Held sale not found');
        return;
      }
      
      // Get the sale data before deletion for API call
      const saleToDelete = localHeldSales[saleIndex];
      
      // Remove the held sale from the local array
      const updatedHeldSales = localHeldSales.filter((_, index) => index !== saleIndex);
      
      // Update local state immediately for UI feedback
      setLocalHeldSales(updatedHeldSales);
      
      // Update parent held sales state - this saves to localStorage and updates the parent
      if (updateHeldSales && typeof updateHeldSales === 'function') {
        updateHeldSales(updatedHeldSales);
        console.log('Held sale deleted and parent state updated successfully');
      } else {
        console.error('updateHeldSales function not provided! Deletion will not persist.');
        toast.error('Unable to save changes. Please refresh and try again.');
        // Revert local state since we can't persist
        setLocalHeldSales(heldSales);
        return;
      }
      
      // Show success notification (don't let these fail the main operation)
      try {
        notificationService.showHeldSaleDeletedNotification(saleId);
      } catch (notificationError) {
        console.error('Error showing held sale deleted notification:', notificationError);
      }
      
      try {
        addNotification('Held Sale Deleted', `Held sale ${saleId} was successfully deleted`, 'success');
      } catch (addNotificationError) {
        console.error('Error adding held sale deleted notification:', addNotificationError);
      }
      
      // Update notification status to approved after successful deletion
      try {
        // Find the OTP notification for this action and mark it as approved
        const notifications = notificationService.getNotifications();
        console.log('🔍 Looking for OTP notification with saleId:', saleId);
        console.log('🔍 Available notifications:', notifications.map(n => ({
          id: n.id,
          title: n.title,
          actionType: n.metadata?.actionType,
          itemName: n.metadata?.itemName,
          isOTP: n.metadata?.isOTP
        })));
        
        // Find the most recent OTP notification for delete_held_sale action
        const otpNotifications = notifications.filter(n => 
          n.metadata?.isOTP && 
          n.metadata?.actionType === 'delete_held_sale' &&
          n.title?.includes('Verification Code')
        );
        
        // Get the most recent one (first in the array since they're sorted by newest first)
        const otpNotification = otpNotifications[0];
        
        if (otpNotification) {
          await notificationApiService.markAsApproved(otpNotification.id);
          console.log('✅ Notification marked as approved after held sale deletion');
          
          // Update local notification state
          const updatedNotifications = notifications.map(notification => 
            notification.id === otpNotification.id 
              ? { ...notification, status: 'approved' }
              : notification
          );
          notificationService.notifications = updatedNotifications;
          notificationService.saveNotifications();
          
          // Force refresh notifications in the UI
          await notificationService.loadNotifications();
        } else {
          console.log('❌ No OTP notification found to mark as approved');
        }
      } catch (statusError) {
        console.error('Error updating notification status:', statusError);
      }
      
      // Show success toast (don't let this fail the main operation)
      try {
        toast.success(`Held sale ${saleId} deleted successfully`);
      } catch (toastError) {
        console.error('Error showing success toast:', toastError);
      }
      
    } catch (error) {
      console.error('Error deleting held sale:', error);
      toast.error('Failed to delete held sale. Please try again.');
      
      // Revert local state on error
      setLocalHeldSales(heldSales);
    } finally {
      setDeletingSaleId(null);
    }
  };

  const showOTPConfirmation = (actionType, itemData) => {
    console.log('🔐 showOTPConfirmation called:', { actionType, itemData });
    
    // Show OTP confirmation modal directly
    setOtpAction({ type: actionType, ...itemData });
    setOtpItemData(itemData);
    setShowOTPModal(true);
    console.log('🔐 OTP modal should be opening...');
  };


  
  return (
    <div 
      className="flex flex-col relative" 
      style={{
        height: '100vh',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Top Header Bar - Compact */}
      <div className="bg-green-600 text-white p-2 flex items-center justify-between relative z-30">
        <div className="flex items-center space-x-8">
          <h1 className="text-lg font-bold">Point of Sales</h1>
          <div className="flex items-center space-x-6 text-sm">
            <span>PCS: {formatCurrency(selectedProduct ? (parseFloat(selectedProduct.selling_price_pcs) || 0) : 0)}</span>
            <span>CTN: {formatCurrency(selectedProduct ? (parseFloat(selectedProduct.selling_price_ctn) || 0) : 0)}</span>
            <span>{currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} {currentTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })}</span>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <span>Transactions : {transactionCount}</span>
          <span>Cashier : {user?.name || user?.username || 'Unknown'}</span>
          {isLoadingProducts ? (
            <span className="text-yellow-300 text-sm animate-pulse">Loading products...</span>
          ) : isInQuantityFlow ? (
            <span className="text-yellow-200 text-sm font-semibold">
              Qty Flow: {selectedProductName}
            </span>
          ) : filteredProducts.length > 0 && (
            <span className="text-green-200 text-sm">
              {searchTerm ? `Filtered: ${filteredProducts.length}` : `Products: ${filteredProducts.length}`}
            </span>
          )}
          
          <button 
            onClick={handleReturnSale}
            className="bg-gradient-to-r from-blue-500 to-purple-600  hover:bg-green-600 px-3 py-1 rounded text-sm font-medium transition-colors relative z-20"
          >
            Return Sale
          </button>
          <button 
            onClick={() => {
              showConfirmation(
                'exit-pos',
                null,
                'Exit POS',
                'Are you sure you want to exit the POS system? Any unsaved changes will be lost.'
              );
            }}
            className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-sm font-medium transition-colors relative z-20"
          >
            Exit POS
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row min-h-0 overflow-hidden flex-1">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col relative z-10">
          {/* Product Search and Input Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 shadow-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 lg:space-x-6">
              <div className="w-full sm:w-2/3 lg:w-1/2 flex items-center space-x-2 bg-white rounded px-3 h-10 py-1 relative search-container">
                <Search size={16} className="text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Filter products by name or ID..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleSearchFocus}
                  onClick={() => {
                    // Activate search functionality when user clicks on search field
                    if (isInQuantityFlow) {
                      setIsInQuantityFlow(false);
                      setSearchTerm('');
                      setSelectedProductName('');
                      console.log('Search functionality activated - quantity flow disabled');
                    }
                  }}
                  className={`flex-1 outline-none text-sm ${
                    isInQuantityFlow 
                      ? 'text-gray-800 font-bold bg-yellow-50 border-2 border-yellow-400 cursor-pointer' 
                      : 'text-gray-700'
                  }`}
                />
                {isSearching && (
                  <div className="absolute right-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
                
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                <div className="flex items-center space-x-2">
                  <label className="text-white font-bold text-sm">Qty: </label>
                  <input 
                    id="quantity-input"
                    type="number" 
                    step="0.01"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                    onFocus={(e) => {
                      setIsQuantityFocused(true);
                      e.target.select(); // Highlight the text
                    }}
                    onBlur={() => setIsQuantityFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const uomSelect = document.getElementById('uom-select');
                        if (uomSelect) {
                          // Focus the element and add visual feedback
                          uomSelect.focus();
                          
                          // Add visual feedback to show it's selected
                          uomSelect.style.border = '2px solid #3b82f6';
                          uomSelect.style.backgroundColor = '#dbeafe';
                          uomSelect.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                          
                          setTimeout(() => {
                            uomSelect.style.border = '';
                            uomSelect.style.backgroundColor = '';
                            uomSelect.style.boxShadow = '';
                          }, 1500);
                        }
                      }
                    }}
                    className="w-20 sm:w-18 lg:w-14 px-2 py-1 rounded border-none outline-none text-sm h-8"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-white font-bold text-sm">UoM: </label>
                  <select 
                    id="uom-select"
                    tabIndex="0"
                    value={uomValue}
                    onChange={(e) => setUomValue(e.target.value)}
                    onFocus={(e) => {
                      // For select elements, we can't use select() but we can ensure focus
                      e.target.focus();
                      setIsUomFocused(true);
                      console.log('UoM field focused');
                      
                      // Add visual feedback when focused
                      e.target.style.border = '2px solid #3b82f6';
                      e.target.style.backgroundColor = '#dbeafe';
                    }}
                    onBlur={(e) => {
                      // Remove visual feedback when focus is lost
                      setIsUomFocused(false);
                      e.target.style.border = '';
                      e.target.style.backgroundColor = '';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        console.log('Enter pressed in UoM field - adding to cart');
                        addToCart();
                        
                        // Exit quantity flow mode and clear search field
                        setIsInQuantityFlow(false);
                        setSearchTerm('');
                        setSelectedProductName('');
                        
                        // After adding to cart, focus back on quantity for next sequence
                        setTimeout(() => {
                          const qtyInput = document.getElementById('quantity-input');
                          if (qtyInput) {
                            qtyInput.focus();
                            qtyInput.select(); // Highlight the text for next entry
                          }
                        }, 100);
                      }
                    }}
                    className="w-24 sm:w-20 lg:w-auto px-2 py-1 rounded border-none outline-none text-sm h-8"
                  >
                    <option>Ctn</option>
                    <option>Pcs</option>
                  </select>
                </div>
                <div className="text-white font-bold text-sm">
                  Current Qty (CTN) : {selectedProduct ? (selectedProduct.quantity || 0).toFixed(2) : 0}
                </div>
              </div>
            </div>
          </div>

          {/* Product Display */}
          <div className="bg-gradient-to-br from-white to-gray-50 overflow-y-auto min-h-0 flex-1 shadow-inner">
            {filteredProducts.length > 0 ? (
              <div className="p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {isInQuantityFlow 
                      ? `Selected: ${selectedProductName} (${filteredProducts.length} products available)`
                      : searchTerm 
                        ? `Filtered Results (${filteredProducts.length})` 
                        : `All Products (${filteredProducts.length})`
                    }
                  </div>
                  {isInQuantityFlow ? (
                    <div className="text-xs text-yellow-600 font-semibold">
                      Quantity Flow Active
                    </div>
                  ) : searchTerm && (
                    <div className="text-xs text-gray-500">
                      Filtering by: "{searchTerm}"
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts.map((product, index) => (
                    <div 
                      key={product.id}
                      className={`bg-white rounded-lg p-4 shadow-md border-2 cursor-pointer transition-all hover:shadow-lg transform hover:scale-105 ${
                        selectedProduct?.id === product.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ pointerEvents: 'auto', zIndex: 50 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Quick add to cart and focus on quantity field
                        try {
                          // Display product name in search field first (bold and highlighted)
                          setSearchTerm(product.name);
                          setSelectedProductName(product.name);
                          setIsInQuantityFlow(true);
                          console.log(`Product name set in search field: ${product.name}`);
                          
                          // Set the product as selected
                          handleProductSelect(product);
                          
                          // Set default values for quick add
                          setQuantity(1);
                          setUomValue('Ctn');
                          
                          // Immediately add to cart
                          addToCart();
                          
                          // Focus on quantity field for user input
                          setTimeout(() => {
                            const qtyInput = document.getElementById('quantity-input');
                            if (qtyInput) {
                              qtyInput.focus();
                              qtyInput.select(); // Highlight the text
                            }
                          }, 100);
                          
                          // Show quick feedback
                          console.log(`Added ${product.name} to cart - focus on quantity`);
                          
                        } catch (error) {
                          console.error('Error adding product to cart:', error);
                        }
                      }}
                    >
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mx-auto mb-3 flex items-center justify-center overflow-hidden">
                          {(() => {
                            const imageSrc = product.image_url;
                            const isBase64 = imageSrc && (imageSrc.startsWith('data:') || imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KGgo'));
                            
                            return imageSrc ? (
                              <img 
                                src={isBase64 ? (imageSrc.startsWith('data:') ? imageSrc : `data:image/jpeg;base64,${imageSrc}`) : imageSrc}
                                alt={product.name}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null;
                          })()}
                          <div 
                            className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
                            style={{ display: product.image_url ? 'none' : 'flex' }}
                          >
                            <span className="text-white font-bold text-lg">
                              {product.name?.charAt(0)?.toUpperCase() || 'P'}
                            </span>
                          </div>
                        </div>
                        <h3 className="font-bold text-sm text-gray-800 mb-2 truncate" title={product.name}>
                          {product.name}
                        </h3>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">CTN:</span>
                            <span className="font-semibold text-green-600">{formatCurrency(product.selling_price_ctn)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">PCS:</span>
                            <span className="font-semibold text-green-600">{formatCurrency(product.selling_price_pcs)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Stock:</span>
                            <span className="font-semibold text-blue-600">{(parseFloat(product.quantity) || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : isLoadingProducts ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-[400px] h-[400px] bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-8 mb-8">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                      </div>
                      <div className="text-gray-500/70 font-bold text-3xl mb-4">LOADING</div>
                      <div className="text-gray-500/70 text-xl font-semibold">PRODUCTS</div>
                      <div className="text-gray-500/50 text-sm mt-2">Please wait...</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-[400px] h-[400px] bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-8 mb-8">
                        <Search size={64} className="text-gray-400/60" />
                        <Package size={64} className="text-gray-400/60" />
                        <ShoppingCart size={64} className="text-gray-400/60" />
                      </div>
                      <div className="text-gray-500/70 font-bold text-5xl mb-4">
                        {searchTerm ? 'NO RESULTS' : 'NO PRODUCTS'}
                    </div>
                      <div className="text-gray-500/70 text-2xl font-semibold">
                        {searchTerm ? 'FOUND' : 'LOADED'}
                      </div>
                      <div className="text-gray-500/50 text-sm mt-2">
                        {searchTerm ? `No products match "${searchTerm}"` : 'Products will appear here when loaded'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Cart and Payment */}
        <div className="w-full lg:w-[36rem] bg-gradient-to-br from-white to-gray-50 flex flex-col border-t-2 lg:border-t-0 lg:border-l-2 border-gray-300">
          
          {/* Cart Header - Fixed */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white p-2 shadow-lg sticky top-0 z-10">
            <div className="flex items-center space-x-3">
              <ShoppingCart size={20} className="text-yellow-300" />
              <h2 className="font-bold text-lg">Shopping Cart</h2>
              <span className="bg-yellow-400 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
                {cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'}
              </span>
              </div>
            </div>

          {/* Cart Items Section */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            {/* Cart Items */}
            <div className="bg-gradient-to-br from-white to-gray-50 overflow-y-auto flex-1 shadow-inner">
              {cartItems.length === 0 ? (
                <div className="flex items-center justify-center h-full min-h-[500px]">
                  <div className="text-center">
                    <div className="w-[450px] h-[450px] bg-gradient-to-br from-red-500/10 to-blue-600/10 rounded-full flex items-center justify-center mx-auto mb-8">
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-8 mb-12">
                          <Monitor size={72} className="text-gray-400/60" />
                          <Barcode size={72} className="text-gray-400/60" />
                          <ShoppingBag size={72} className="text-gray-400/60" />
                        </div>
                        <div className="text-gray-500/70 font-bold text-7xl mb-6 tracking-wider">NEXPOS</div>
                        <div className="text-gray-500/70 text-4xl font-semibold tracking-wide">SYSTEM</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-2">
                  <div className="space-y-2">
                    {cartItems.map((item, index) => (
                      <div key={item.id} className="bg-gradient-to-r from-white to-blue-50 rounded-lg p-3 shadow-md border border-blue-200 hover:border-blue-400 transition-all duration-200 hover:shadow-lg">
                        <div className="flex items-center justify-between">
                          {/* Product Info Section */}
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
                              {(() => {
                                const imageSrc = item.product.image_url;
                                const isBase64 = imageSrc && (imageSrc.startsWith('data:') || imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KGgo'));
                                
                                return imageSrc ? (
                                  <img 
                                    src={isBase64 ? (imageSrc.startsWith('data:') ? imageSrc : `data:image/jpeg;base64,${imageSrc}`) : imageSrc}
                                    alt={item.product.name}
                                    className="w-full h-full object-cover rounded-lg"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null;
                              })()}
                              <div 
                                className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
                                style={{ display: item.product.image_url ? 'none' : 'flex' }}
                              >
                                <span className="text-white font-bold text-sm">
                                  {item.product.name?.charAt(0)?.toUpperCase() || 'P'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-sm text-gray-800 truncate" title={item.product.name}>
                                {item.product.name}
                              </h3>
                              <div className="flex items-center space-x-3 mt-0.5">
                                <span className="text-xs text-gray-600">
                                  Qty: <span className="font-bold text-blue-600">{item.quantity}</span>
                                </span>
                                <span className="text-xs text-gray-600">
                                  UoM: <span className="font-bold text-green-600">{item.uom}</span>
                                </span>
                          </div>
                          </div>
                          </div>

                          {/* Price and Actions Section */}
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-xs text-gray-600">Unit</div>
                              <div className="font-bold text-sm text-blue-600">
                            {formatCurrency(item.price || 0)}
                          </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="text-xs text-gray-600">Total</div>
                              <div className="font-bold text-base text-green-600">
                            {formatCurrency(item.amount || 0)}
                          </div>
                            </div>

                            {/* Action Button */}
                            <div className="flex items-center">
                              <button 
                                onClick={() => {
                                  setSelectedCartItem(item);
                                  setShowItemModal(true);
                                }}
                                className="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm"
                              >
                                <Settings size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Ultra Compact Payment Section */}
          <div className="p-0.5 space-y-0.5 flex-shrink-0 border-t border-gray-300 -mt-20">
            {/* Total Amount - Compact */}
            <div className="bg-gradient-to-r from-black to-gray-800 text-white p-1 rounded shadow border border-yellow-400">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="font-bold text-2xl text-yellow-300">TOTAL</div>
                <div className="text-yellow-400 font-bold text-2xl text-center">
                {formatCurrency(cartItems.reduce((total, item) => total + (item.amount || 0), 0))}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-black text-white p-1 rounded shadow">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="font-bold text-2xl">Method</div>
                <select
                  id="payment-method-select"
                  value={paymentMethod}
                  onChange={(e) => {
                    console.log('Payment method changed to:', e.target.value);
                    setPaymentMethod(e.target.value);
                  }}
                  onClick={() => console.log('Payment dropdown clicked')}
                  className="w-full bg-blue-500 hover:bg-blue-600 py-0.5 rounded font-bold text-2xl text-white text-center cursor-pointer"
                >
                  <option value="Cash">Cash</option>
                  <option value="Mobile Money">Mobile Money</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Visa">Visa</option>
                </select>
              </div>
              </div>
              
            {/* Paid Amount */}
            <div className="bg-gradient-to-r from-black to-gray-800 text-white p-1 rounded shadow border border-yellow-400">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="font-bold text-2xl text-yellow-300">Paid</div>
                <div className="text-yellow-400 font-bold text-2xl text-center">
                <input
                    id="paidAmount-input"
                  type="number"
                  min="0"
                    step="0.01"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                    onFocus={(e) => {
                      // Ensure text is selected after a small delay to handle focus timing
                      setTimeout(() => {
                        e.target.select();
                        e.target.setSelectionRange(0, e.target.value.length);
                      }, 10);
                      e.target.style.backgroundColor = 'rgba(255, 255, 0, 0.2)';
                      e.target.style.border = '2px solid #fbbf24';
                      e.target.style.borderRadius = '4px';
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.border = 'none';
                      e.target.style.borderRadius = '0';
                    }}
                    className="bg-transparent text-yellow-400 font-bold text-2xl text-center border-none outline-none w-full transition-all duration-200"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Change Amount */}
            <div className="bg-red-600 text-white p-1 rounded shadow">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="font-bold text-3xl">Change</div>
                <div className="font-bold text-3xl text-center">
                {formatCurrency(Math.max(0, paidAmount - cartItems.reduce((total, item) => total + (item.amount || 0), 0)))}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Action Buttons */}
          <div className="p-2 space-y-2 flex-shrink-0 border-t border-gray-300">
            <button 
              onClick={handleProcessSaleView2}
              disabled={isProcessingSale || cartItems.length === 0}
              className={`w-full py-3 rounded font-bold text-base transition-colors shadow transform hover:scale-105 ${
                isProcessingSale || cartItems.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isProcessingSale ? 'Processing...' : 'Process Sales'}
            </button>
            
            {/* Secondary Actions in Grid */}
            <div className="grid grid-cols-3 gap-2 mb-10">
              <button 
                onClick={handleHoldSales}
                disabled={cartItems.length === 0}
                className={`py-2 rounded font-bold text-sm transition-colors shadow transform hover:scale-105 ${
                  cartItems.length === 0
                    ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Hold Sales
              </button>
              <button 
                onClick={() => setShowHeldSalesModalLocal(true)}
                disabled={localHeldSales.length === 0}
                className={`py-2 rounded font-bold text-sm transition-colors shadow transform hover:scale-105 ${
                  localHeldSales.length === 0
                    ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Held Sales ({localHeldSales.length})
              </button>
              <button 
                onClick={() => {
                  if (cartItems.length > 0) {
                    showOTPConfirmation('clear_cart', {
                      itemCount: cartItems.length,
                      totalAmount: cartItems.reduce((total, item) => total + (item.amount || 0), 0),
                      itemName: `Clear ${cartItems.length} items from cart`,
                      itemDetails: {
                        summary: `Total: ${formatCurrency(cartItems.reduce((total, item) => total + (item.amount || 0), 0))}`,
                        items: cartItems.map(item => ({
                          name: item.product.name,
                          quantity: item.quantity,
                          uom: item.uom,
                          amount: item.amount
                        }))
                      }
                    });
                  } else {
                    // Cart is already empty, just reset payment states without confirmation
                    setPaidAmount(0);
                    setPaymentMethod('Cash');
                    toast.success('Cart is already empty');
                  }
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded font-bold text-sm transition-colors shadow transform hover:scale-105"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Cart Item Edit Modal */}
      {showItemModal && selectedCartItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Edit Cart Item</h3>
              <button
                onClick={() => setShowItemModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {selectedCartItem.product.name?.charAt(0)?.toUpperCase() || 'P'}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">{selectedCartItem.product.name}</h4>
                  <p className="text-sm text-gray-600">
                    Unit Price: <span className="font-bold text-blue-600">{formatCurrency(selectedCartItem.price)}</span> 
                    <span className="text-xs text-gray-500 ml-2">({selectedCartItem.uom})</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const newQuantity = Math.max(0.01, selectedCartItem.quantity - 0.01);
                      const currentPrice = selectedCartItem.price;
                      const updatedItem = { ...selectedCartItem, quantity: newQuantity, amount: newQuantity * currentPrice };
                      setCartItems(prev => prev.map(cartItem => 
                        cartItem.id === selectedCartItem.id 
                          ? updatedItem
                          : cartItem
                      ));
                      setSelectedCartItem(updatedItem);
                    }}
                    className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center font-bold transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={selectedCartItem.quantity}
                    onChange={(e) => {
                      const newQuantity = parseFloat(e.target.value) || 0.01;
                      const currentPrice = selectedCartItem.price;
                      const updatedItem = { ...selectedCartItem, quantity: newQuantity, amount: newQuantity * currentPrice };
                      setCartItems(prev => prev.map(cartItem => 
                        cartItem.id === selectedCartItem.id 
                          ? updatedItem
                          : cartItem
                      ));
                      setSelectedCartItem(updatedItem);
                    }}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-semibold cursor-text"
                    placeholder="Enter quantity"
                    autoComplete="off"
                  />
                  <button
                    onClick={() => {
                      const newQuantity = selectedCartItem.quantity + 0.01;
                      const currentPrice = selectedCartItem.price;
                      const updatedItem = { ...selectedCartItem, quantity: newQuantity, amount: newQuantity * currentPrice };
                      setCartItems(prev => prev.map(cartItem => 
                        cartItem.id === selectedCartItem.id 
                          ? updatedItem
                          : cartItem
                      ));
                      setSelectedCartItem(updatedItem);
                    }}
                    className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit of Measure</label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const newPrice = selectedCartItem.product.selling_price_pcs || selectedCartItem.price;
                      const updatedItem = { 
                        ...selectedCartItem, 
                        uom: 'Pcs',
                        price: newPrice,
                        amount: selectedCartItem.quantity * newPrice
                      };
                      setCartItems(prev => prev.map(cartItem => 
                        cartItem.id === selectedCartItem.id 
                          ? updatedItem
                          : cartItem
                      ));
                      setSelectedCartItem(updatedItem);
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                      selectedCartItem.uom === 'Pcs'
                        ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Pieces (Pcs)
                  </button>
                  <button
                    onClick={() => {
                      const newPrice = selectedCartItem.product.selling_price_ctn || selectedCartItem.price;
                      const updatedItem = { 
                        ...selectedCartItem, 
                        uom: 'Ctn',
                        price: newPrice,
                        amount: selectedCartItem.quantity * newPrice
                      };
                      setCartItems(prev => prev.map(cartItem => 
                        cartItem.id === selectedCartItem.id 
                          ? updatedItem
                          : cartItem
                      ));
                      setSelectedCartItem(updatedItem);
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                      selectedCartItem.uom === 'Ctn'
                        ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Carton (Ctn)
                  </button>
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Total Amount</div>
                <div className="font-bold text-xl text-green-600">
                  {formatCurrency(selectedCartItem.quantity * selectedCartItem.price)}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowItemModal(false);
                  showOTPConfirmation('remove_cart_item', {
                    itemId: selectedCartItem.id,
                    itemName: selectedCartItem.product.name,
                    itemDetails: {
                      summary: `Qty: ${selectedCartItem.quantity} ${selectedCartItem.uom} - ${formatCurrency(selectedCartItem.amount)}`,
                      items: [{
                        name: selectedCartItem.product.name,
                        quantity: selectedCartItem.quantity,
                        uom: selectedCartItem.uom,
                        amount: selectedCartItem.amount
                      }]
                    }
                  });
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Remove Item
              </button>
              <button
                onClick={() => setShowItemModal(false)}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Held Sales Modal */}
      {showHeldSalesModalLocal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 shadow-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-800">Held Sales</h3>
                  <p className="text-sm text-gray-600">{localHeldSales.length} sales on hold</p>
                </div>
              </div>
              <button
                onClick={() => setShowHeldSalesModalLocal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[60vh]">
              {localHeldSales.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Held Sales</h3>
                  <p className="text-gray-500">No sales are currently on hold.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {localHeldSales.map((heldSale, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">Held Sale #{index + 1}</h3>
                          <p className="text-sm text-gray-600">Reference: {heldSale.reference || `HOLD-${index + 1}`}</p>
                          <p className="text-sm text-gray-600">{heldSale.timestamp || 'Recently held'}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">
                            {formatCurrency(heldSale.total || 0)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {heldSale.items?.length || 0} items
                          </div>
                        </div>
                      </div>
                      
                      {/* Items List */}
                      {heldSale.items && heldSale.items.length > 0 && (
                        <div className="bg-white rounded border border-gray-200 mb-3">
                          <div className="p-3 border-b border-gray-200">
                            <div className="grid grid-cols-4 gap-4 text-sm font-semibold text-gray-700">
                              <div>Product</div>
                              <div>Qty</div>
                              <div>UOM</div>
                              <div>Amount</div>
                            </div>
                          </div>
                          <div className="max-h-32 overflow-y-auto">
                            {heldSale.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="p-3 border-b border-gray-100 last:border-b-0">
                                <div className="grid grid-cols-4 gap-4 text-sm">
                                  <div className="font-medium text-gray-900 truncate">
                                    {item.product?.name || item.name || 'Unknown Product'}
                                  </div>
                                  <div className="text-gray-600">{item.quantity || 0}</div>
                                  <div className="text-gray-600">{item.uom || 'Pcs'}</div>
                                  <div className="text-gray-600">{formatCurrency(item.amount || 0)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            if (handleLoadHeldSale && typeof handleLoadHeldSale === 'function') {
                              handleLoadHeldSale(heldSale);
                              setShowHeldSalesModalLocal(false);
                            } else {
                              toast.error('Load function not available');
                              console.error('handleLoadHeldSale function not provided');
                            }
                          }}
                          disabled={cartItems.length > 0}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            cartItems.length > 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-500 hover:bg-green-600 text-white'
                          }`}
                        >
                          Load
                        </button>
                        <button
                          onClick={() => {
                            setShowHeldSalesModalLocal(false);
                            showOTPConfirmation('delete_held_sale', {
                              saleId: heldSale.reference || `HOLD-${index + 1}`,
                              itemName: `Held Sale #${index + 1}`,
                              itemDetails: {
                                summary: `Total: ${formatCurrency(heldSale.total || 0)} - ${heldSale.items?.length || 0} items`,
                                items: (heldSale.items || []).map(item => ({
                                  name: item.product?.name || item.productName || 'Unknown Item',
                                  quantity: item.quantity,
                                  uom: item.uom,
                                  amount: item.amount
                                }))
                              }
                            });
                          }}
                          disabled={deletingSaleId === (heldSale.reference || `HOLD-${index + 1}`)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                            deletingSaleId === (heldSale.reference || `HOLD-${index + 1}`)
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                          }`}
                        >
                          {deletingSaleId === (heldSale.reference || `HOLD-${index + 1}`) ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OTP Confirmation Modal */}
      <OTPConfirmationModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setOtpAction(null);
          setOtpItemData(null);
        }}
        onConfirm={handleOTPConfirm}
        actionType={otpAction?.type}
        itemName={otpItemData?.itemName}
        itemDetails={otpItemData?.itemDetails}
        title={
          otpAction?.type === 'remove_cart_item' ? 'Remove Cart Item' :
          otpAction?.type === 'clear_cart' ? 'Clear Cart' :
          'Delete Held Sale'
        }
        description={
          otpAction?.type === 'remove_cart_item' 
            ? 'Please enter the OTP to confirm removal of this item from the cart.'
            : otpAction?.type === 'clear_cart'
            ? 'Please enter the OTP to confirm clearing all items from the cart.'
            : 'Please enter the OTP to confirm deletion of this held sale.'
        }
      />

    </div>
  );
};

export default POSView2;
